/* File: src/components/game/GameResults.js */
import React, { useState, useCallback, useEffect } from 'react';
import FocusMeter from '../dashboard/FocusMeter';
import FeatureIntroMessage from './FeatureIntroMessage';
import CoachingMessage from './CoachingMessage';
import { getTierName, formatCombinedDifficulty } from '../../contexts/gameHelpers';
import { formatDuration } from '../dashboard/utils/formatters';
import { useProfile } from '../../contexts/ProfileContext';
import CommentModal from '../ui/CommentModal';
// ** NEW/VERIFIED ** Import sanitizeTag if needed for local sanitization (though addJournalTag likely handles it)
import { sanitizeTag } from '../../contexts/profile/profileUtils'; // Adjust path if needed

// Font Awesome Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBook, faPlay, faRotateRight, faVideo, faHouse,
    faChartLine, faLightbulb, faChartBar // <<< faCaretLeft removed
} from '@fortawesome/free-solid-svg-icons';

/**
 * Redesigned GameResults Component - Shows performance after a game session.
 * Uses dynamic thresholds received from the backend for display.
 * Restored original look and feel, fallback message, and detailed stats content.
 * FIXED: Play Again and Watch Replay button handlers.
 * ADDED: d' tag in header, improved performance scale reference with ranges and specific highlighting.
 * MODIFIED: Refined styling for user's d' score display and range highlighting.
 */
const GameResults = ({
  gameResults,
  feedbackMessage,
  getFeatureIntroMessage,
  onDashboard,
  onNavigate, // Function to navigate to different routes/views
  onRetryLevel, // Function to retry the *same* level (N.00)
  onStartNextSession, // Function to start the *next determined* level (N.DD) or play again
  resetResponses // Function to clear response arrays before next game
}) => {
    // --- Context & State ---
    const { activeProfile, addJournalEntry, addJournalTag } = useProfile();
    const [isSavingToJournal, setIsSavingToJournal] = useState(false);
    const [journalSaveStatus, setJournalSaveStatus] = useState(null);
    const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');

    // --- Data Extraction ---
    // Use effect with dependency array for debugging if needed
    useEffect(() => {
        // console.log("GameResults received:", gameResults);
    }, [gameResults]);

    const {
        replayData = null, sessionId = null, isPlayground = false, isChallengeAttempt = false,
        isPartialResult = false, trialsCompleted = 0, totalTrialsPlanned = 0,
        oldNBack = 2, oldDifficulty = 0.0, newNBack = 2, newDifficulty = 0.0,
        overallDprime = null, overallAccuracy = null, visualAccuracy = null,
        visualCorrect = null, visualTargetCount = null, visualFalseAlarms = null,
        visualDprime = null, audioAccuracy = null, audioCorrect = null,
        audioTargetCount = null, audioFalseAlarms = null, audioDprime = null,
        excellentThreshold = null, goodThreshold = null, fairThreshold = null,
        showRetryOption = false, showReport = true,
        durationMs = null, username = 'Player',
    } = gameResults || {};

    // --- Derived data ---
    const hasValidReplayDataStructure = !!replayData?.settings && Array.isArray(replayData.events) && replayData.startTime !== undefined;
    // Ensure sessionId exists for standard replays, or allow if it's a challenge attempt (which might have replay data but no saved sessionId yet)
    const canReplay = (!!sessionId || (isChallengeAttempt && hasValidReplayDataStructure)) && hasValidReplayDataStructure;
    const canJournal = !isPlayground && !!sessionId && !isChallengeAttempt;

    // --- Formatting & Logic ---
    const introMessage = typeof getFeatureIntroMessage === 'function' ? getFeatureIntroMessage(newDifficulty) : null;
    const oldCombinedValue = formatCombinedDifficulty(oldNBack, oldDifficulty);
    const newCombinedValue = formatCombinedDifficulty(newNBack, newDifficulty);
    const formatNum = (num, decimals = 2) => ((typeof num === 'number' && Number.isFinite(num)) ? num.toFixed(decimals) : 'N/A');
    const formatAcc = (num) => formatNum(num, 1);

    const sessionMetSuccessThreshold = overallDprime !== null && goodThreshold !== null && overallDprime >= goodThreshold;
    const isFailedChallengeAttempt = isChallengeAttempt === true && !sessionMetSuccessThreshold;

    // Dynamic button text
    let playAgainButtonText = "Play Again";
    if (isPlayground) { playAgainButtonText = "Play Again"; }
    else if (isFailedChallengeAttempt) { playAgainButtonText = `Try Level ${oldCombinedValue}`; } // Retry the *same* challenge level
    else { playAgainButtonText = `Play Level ${newCombinedValue}`; } // Proceed to next level

    // --- Performance Assessment (Using Dynamic Thresholds) ---
    let veryGoodT = null; // Calculate Very Good threshold relative to Good and Excellent for display
    const thresholdsValid = excellentThreshold !== null && goodThreshold !== null && fairThreshold !== null; // Check if all needed thresholds are valid numbers
    if (thresholdsValid) {
        veryGoodT = goodThreshold + (excellentThreshold - goodThreshold) * 0.6;
    }

    const getPerformanceCategory = (dprime, excellentT, goodT, fairT, calculatedVeryGoodT) => {
        // Check for null thresholds explicitly before using them
        const useDynamicThresholds = excellentT !== null && goodT !== null && fairT !== null && calculatedVeryGoodT !== null;
        if (dprime === null || typeof dprime !== 'number') return 'unknown';

        if (useDynamicThresholds) {
            if (dprime >= excellentT) return 'excellent';
            // Use calculatedVeryGoodT if thresholds are valid
            if (dprime >= calculatedVeryGoodT) return 'very-good';
            if (dprime >= goodT) return 'good';
            if (dprime >= fairT) return 'fair';
            return 'needs-improvement';
        } else {
             // Fallback interpretation if dynamic thresholds are missing
            console.warn("GameResults: Dynamic thresholds missing or invalid. Using fallback interpretation for category.");
            if (dprime >= 2.0) return 'good'; // Use a broader 'good' category for fallback
            if (dprime >= 1.0) return 'fair';
            return 'needs-improvement';
        }
    };
    const performanceCategory = getPerformanceCategory(overallDprime, excellentThreshold, goodThreshold, fairThreshold, veryGoodT); // Pass calculated veryGoodT

    const getPerformanceDisplay = (category) => {
        switch(category) {
            case 'excellent': return { text: 'Excellent', colorClass: 'text-green-600 bg-green-50 border border-green-200' };
            case 'very-good': return { text: 'Very Good', colorClass: 'text-blue-600 bg-blue-50 border border-blue-200' };
            case 'good': return { text: 'Good', colorClass: 'text-indigo-600 bg-indigo-50 border border-indigo-200' };
            case 'fair': return { text: 'Fair', colorClass: 'text-amber-600 bg-amber-50 border border-amber-200' };
            case 'needs-improvement': return { text: 'Needs Improvement', colorClass: 'text-red-600 bg-red-50 border border-red-200' };
            default: return { text: 'Unknown', colorClass: 'text-gray-600 bg-gray-50 border border-gray-200' };
        }
    };
    const performanceDisplay = getPerformanceDisplay(performanceCategory);

    // --- Event Handlers ---

    // Handler for the 'Retry Level N.00' button
    const handleRetry = useCallback(() => {
        console.log("GameResults: Retry Level clicked.");
        if (typeof resetResponses === 'function') {
            resetResponses();
        }
        if (typeof onRetryLevel === 'function') {
            onRetryLevel(); // This should trigger the logic to restart at N.00
        } else {
            console.error("GameResults: onRetryLevel function not provided.");
        }
    }, [onRetryLevel, resetResponses]); // Include resetResponses dependency

    // ** FIXED: Handler for the 'Watch Replay' button **
    const handleReplay = useCallback(() => {
        console.log("GameResults: Watch Replay clicked.");
        if (!canReplay) {
            console.warn("GameResults: Replay not available for this session.");
            // TODO: Optionally show a user-facing message/disable button more clearly
            return;
        }
        if (typeof onNavigate !== 'function') {
            console.error("GameResults: Cannot navigate to replay - onNavigate function is missing.");
            return;
        }

        // Get the sessionId which is the identifier for the replay
        const replayId = sessionId;

        if (!replayId) {
             console.error("GameResults: Cannot navigate to replay - Session ID is missing. Replay Data:", replayData);
             return;
        }

        console.log(`GameResults: Navigating to replay viewer for Session ID: ${replayId}`);
        // Construct the path for the replay viewer route (ensure this matches App.js routing)
        const replayPath = `/replay/${replayId}`;
        onNavigate(replayPath); // Pass the path to the navigation function

    }, [onNavigate, canReplay, sessionId, replayData]); // Add replayData to dependencies if its structure affects canReplay or ID finding

    // ** FIXED: Handler for the 'Play Level N.DD' / 'Play Again' button **
    const handleStartNextSessionAction = useCallback(() => {
        console.log("GameResults: Start Next Session / Play Again button clicked.");

        // Reset response arrays before starting the next game
        if (typeof resetResponses === 'function') {
            resetResponses();
            console.log("GameResults: Called resetResponses.");
        } else {
            console.warn("GameResults: resetResponses function not provided.");
        }

        // Call the function passed via props to start the next game session
        if (typeof onStartNextSession === 'function') {
            onStartNextSession(); // This function should handle setting the correct N/Diff and starting the game flow
             console.log("GameResults: Called onStartNextSession prop.");
        } else {
            console.error("GameResults: Cannot start next session - onStartNextSession function is missing.");
            // As a fallback, maybe go to dashboard? Or show an error. Let's error for now.
            // If `onStartNextSession` isn't provided, the button shouldn't work.
        }
    }, [onStartNextSession, resetResponses]); // Dependencies are the functions it calls

    // --- Journal Handlers --- (FIX Applied)
    const handleOpenJournalModal = useCallback(() => {
       if (isSavingToJournal || journalSaveStatus === 'success' || !canJournal) return;
        setIsCommentModalOpen(true);
    }, [isSavingToJournal, journalSaveStatus, canJournal]);

    const handleCloseJournalModal = useCallback(() => {
        setIsCommentModalOpen(false);
        // Optionally reset save status if modal is closed before success message timeout
        // if (journalSaveStatus === 'error') { setJournalSaveStatus(null); }
    }, []);

    const handleSaveJournalEntry = useCallback(async (comment, userTags = []) => {
        if (!activeProfile || !sessionId || isSavingToJournal || !canJournal) {
             console.warn("Journal save prevented: Missing profile/sessionId, already saving, or cannot journal.");
             return;
        }
        setIsSavingToJournal(true);
        setJournalSaveStatus('saving');
        console.log("Attempting to save journal entry:", { sessionId, comment, userTags });

        // Sanitize user tags BEFORE preparing entryData
        const sanitizedUserTags = Array.isArray(userTags)
            ? [...new Set(userTags.map(tag => sanitizeTag(tag)).filter(Boolean))].sort()
            : [];

        // Prepare journal entry data (ensure all fields are present or handled)
        const entryData = {
            // --- FIX START ---
            // 1. Added 'type' field: This was missing and caused the "Unknown entry type" error.
            type: 'session_summary',
            // 2. Added 'summaryData' field: This provides the necessary session details
            //    for the JournalEntry component to render this type of entry correctly.
            summaryData: {
                // Include all relevant fields that JournalEntry.js might need to display
                // Ensure these keys match what JournalEntry.js expects in its `case 'session_summary'`
                oldNBack: oldNBack ?? null,
                oldDifficulty: oldDifficulty ?? null,
                newNBack: newNBack ?? null,
                newDifficulty: newDifficulty ?? null,
                durationMs: durationMs ?? null,
                trialsCompleted: trialsCompleted ?? 0,
                totalTrialsPlanned: totalTrialsPlanned ?? 0,
                overallDprime: overallDprime ?? null,
                overallAccuracy: overallAccuracy ?? null, // Added for completeness if needed
                visualAccuracy: visualAccuracy ?? null,
                visualCorrect: visualCorrect ?? null,
                visualTargetCount: visualTargetCount ?? null,
                visualFalseAlarms: visualFalseAlarms ?? null,
                visualDprime: visualDprime ?? null,
                audioAccuracy: audioAccuracy ?? null,
                audioCorrect: audioCorrect ?? null,
                audioTargetCount: audioTargetCount ?? null,
                audioFalseAlarms: audioFalseAlarms ?? null,
                audioDprime: audioDprime ?? null,
                excellentThreshold: excellentThreshold ?? null,
                goodThreshold: goodThreshold ?? null,
                fairThreshold: fairThreshold ?? null,
                // Include replayNavData if the replay button in JournalEntry needs it
                // Check if JournalEntry uses `entry.summaryData.replayNavData`
                replayNavData: canReplay ? replayData : null, // Pass full replayData if needed
                // Pass sessionId within summaryData as well if JournalEntry relies on it for replay link
                sessionId: sessionId
            },
            // --- FIX END ---

            // Original fields (ensure they don't conflict with summaryData)
            sessionId: sessionId, // Keep top-level sessionId if needed elsewhere? (Maybe redundant)
            timestamp: new Date(), // Use current time for journal entry
            comment: comment || "", // Ensure comment is string
            tags: sanitizedUserTags, // Use the sanitized tags array
            // Storing these top-level might be redundant now that they are in summaryData,
            // but keep them if other parts of the system query them directly.
            // Consider removing if only summaryData is used for display.
            nBack: oldNBack ?? 'N/A',
            difficulty: oldDifficulty ?? 'N/A',
            // ... other top-level metrics if needed ...
            hasReplay: canReplay, // Keep this flag maybe?
        };

        // --- Removed the separate 'replayDataSnapshot' - passing full replayData in summaryData if needed ---

        // Defensive check: Ensure summaryData exists for this type
        if (!entryData.summaryData) {
            console.error("CRITICAL: summaryData is missing in entryData for session_summary type!");
            setIsSavingToJournal(false);
            setJournalSaveStatus('error'); // Indicate an internal error
            // Maybe set a more specific error message
            // setError("Internal error preparing journal data.");
            return;
        }


        try {
            // Call the function from ProfileContext to add the entry
            const successResult = await addJournalEntry(activeProfile.id, entryData);

            if (successResult && successResult.id) {
                 // Add any new tags to the profile's known tags (using the already sanitized tags)
                 if (sanitizedUserTags.length > 0) {
                     const existingTags = activeProfile.journalTags || [];
                     const newTagsToProfile = sanitizedUserTags.filter(tag => !existingTags.includes(tag));
                     if (newTagsToProfile.length > 0) {
                         // Assuming addJournalTag can handle an array of tags or you loop here
                         await addJournalTag(activeProfile.id, newTagsToProfile);
                     }
                 }

                setJournalSaveStatus('success');
                console.log("Journal entry saved successfully.");
                setTimeout(() => {
                    handleCloseJournalModal();
                     setTimeout(() => setJournalSaveStatus(null), 500);
                }, 1500);
            } else {
                console.error("addJournalEntry call did not return a successful result object. Return value:", successResult);
                throw new Error("addJournalEntry returned unexpected value or failed.");
            }
        } catch (error) {
            console.error("Failed to save journal entry:", error);
            setJournalSaveStatus('error');
        } finally {
            setIsSavingToJournal(false);
        }
    // Ensure all data points used in entryData construction are dependencies
    }, [
        activeProfile, sessionId, isSavingToJournal, canJournal, addJournalEntry, addJournalTag, handleCloseJournalModal,
        oldNBack, oldDifficulty, newNBack, newDifficulty, durationMs, trialsCompleted, totalTrialsPlanned,
        overallDprime, overallAccuracy, visualAccuracy, visualCorrect, visualTargetCount, visualFalseAlarms, visualDprime,
        audioAccuracy, audioCorrect, audioTargetCount, audioFalseAlarms, audioDprime, canReplay, replayData,
        isChallengeAttempt, excellentThreshold, goodThreshold, fairThreshold,
        gameResults // Added dependency as it's used for summaryData
    ]);


    // --- Tab Switch Handler ---
    const handleTabChange = (tab) => { setActiveTab(tab); };

    // --- Render Logic ---
    // Decide if report should be shown at all
    if (showReport === false && !isChallengeAttempt) { // Always show report for challenge attempts
        console.log("GameResults: showReport is false and not a challenge, rendering null.");
        return null;
    }
    if (!gameResults) {
        console.error("GameResults: gameResults prop is null or undefined.");
        return (
            // Restore flex centering here for consistency
            <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4 text-red-600"> {/* Restored min-h-screen and flex centering */}
                Error: Game results data is missing. Cannot display results.
            </div>
        );
    }

    // Determine button layout based on how many are visible
    let bottomButtonCount = 2; // Dashboard + Play Next are baseline
    if (showRetryOption) bottomButtonCount++;
    if (canReplay) bottomButtonCount++;
    // Adjust grid columns for responsiveness. Max 5 seems reasonable.
    const bottomGridColsClass = `sm:grid-cols-${Math.min(bottomButtonCount, 5)}`;

    // --- Main Component Return ---
    return (
        // Re-added `min-h-screen flex items-center justify-center` to restore desktop centering.
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
                {/* Header Section */}
                <div className="px-4 sm:px-6 py-5 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    {/* Left side: Title and Badges */}
                    <div className="flex-1">
                         <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
                            {isPlayground ? 'Playground Results' : isFailedChallengeAttempt ? 'Challenge Attempt' : 'Session Complete'}
                         </h1>
                         {/* Added d' tag */}
                         <div className="mt-1.5 flex flex-wrap gap-2 items-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${performanceDisplay.colorClass}`}>
                                {performanceDisplay.text}
                            </span>
                             {overallDprime !== null && (
                                 <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-300">
                                    d′: {formatNum(overallDprime, 2)}
                                 </span>
                             )}
                             {durationMs !== null && (
                                 <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-300">
                                     {formatDuration(durationMs)}
                                 </span>
                             )}
                            {isPartialResult && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                                    Partial: {trialsCompleted}/{totalTrialsPlanned} trials
                                </span>
                            )}
                            {isPlayground && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-300">
                                    Playground Mode
                                </span>
                            )}
                        </div>
                    </div>
                    {/* Right side: Journal Button (conditional) */}
                    {canJournal && (
                        <div className="flex-shrink-0 mt-2 sm:mt-0">
                            <button
                                onClick={handleOpenJournalModal}
                                disabled={isSavingToJournal || journalSaveStatus === 'success'}
                                className={`inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                    journalSaveStatus === 'success' ? 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 cursor-default' :
                                    journalSaveStatus === 'error' ? 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500' :
                                    'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed'
                                }`}
                                title={journalSaveStatus === 'success' ? "Added to Journal!" : journalSaveStatus === 'error' ? "Error Saving - Click to Retry?" : "Add Notes & Tags to Journal"}
                            >
                                <FontAwesomeIcon icon={faBook} className="w-4 h-4 mr-1.5" />
                                {isSavingToJournal ? 'Saving...' : journalSaveStatus === 'success' ? 'Added' : journalSaveStatus === 'error' ? 'Save Failed' : 'Add to Journal'}
                            </button>
                        </div>
                    )}
                </div>

                {/* Tabs Navigation */}
                <div className="border-b border-gray-200">
                     <nav className="flex -mb-px px-4 sm:px-6" aria-label="Tabs">
                         <button onClick={() => handleTabChange('overview')} className={`py-3 px-4 text-sm font-medium border-b-2 flex items-center ${activeTab === 'overview' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} aria-current={activeTab === 'overview' ? 'page' : undefined}>
                            <FontAwesomeIcon icon={faChartLine} className={`w-4 h-4 mr-2 ${activeTab === 'overview' ? 'text-indigo-500' : 'text-gray-400'}`} /> Overview
                         </button>
                         <button onClick={() => handleTabChange('detailed')} className={`py-3 px-4 text-sm font-medium border-b-2 flex items-center ${activeTab === 'detailed' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} aria-current={activeTab === 'detailed' ? 'page' : undefined}>
                             <FontAwesomeIcon icon={faChartBar} className={`w-4 h-4 mr-2 ${activeTab === 'detailed' ? 'text-indigo-500' : 'text-gray-400'}`} /> Detailed Stats
                         </button>
                         {/* --- Commented out Tips & Progress Tab Button --- */}
                         {/* ... */}
                     </nav>
                </div>

                {/* Tab Content Area */}
                {/* This inner div handles padding; scrolling should be handled within the tab content if needed */}
                <div className="p-4 sm:p-6">
                    {/* Overview Tab Content */}
                    {activeTab === 'overview' && (
                        <div className="animate-[fadeIn_0.3s_ease-in]">
                            {/* 1. Overall Performance Summary Card */}
                            <div className="mb-6 p-4 rounded-lg border border-gray-200 bg-gray-50">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                                    {/* Overall d' */}
                                    <div className="text-center flex-1">
                                        <div className="text-sm text-gray-500 mb-1">Overall Sensitivity (d′)</div>
                                        <div className={`text-3xl font-bold ${performanceDisplay.colorClass.split(' ')[0]}`}>{formatNum(overallDprime, 2)}</div>
                                        <div className="text-xs text-gray-500 mt-1">{performanceDisplay.text}</div>
                                    </div>
                                    {/* Divider */}
                                    <div className="hidden sm:block h-14 w-px bg-gray-200"></div>
                                    {/* Visual/Audio Breakdown */}
                                    <div className="flex-1 grid grid-cols-2 gap-2 text-center">
                                        <div>
                                            <div className="text-sm text-gray-500 mb-1">Visual</div>
                                            <div className="font-bold text-lg text-blue-600">{formatAcc(visualAccuracy)}%</div>
                                            <div className="text-xs text-gray-500">d′: {formatNum(visualDprime, 1)}</div>
                                        </div>
                                        <div>
                                            <div className="text-sm text-gray-500 mb-1">Audio</div>
                                            <div className="font-bold text-lg text-green-600">{formatAcc(audioAccuracy)}%</div>
                                            <div className="text-xs text-gray-500">d′: {formatNum(audioDprime, 1)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                             {/* 2. Level Progression Display (Conditional: Not Playground, Not Failed Challenge) */}
                             {!isPlayground && !isFailedChallengeAttempt && (
                                <div className="mb-6 p-4 rounded-lg border border-gray-200 bg-white">
                                    <h3 className="text-sm font-medium text-gray-700 mb-2">Level Progression</h3>
                                    <div className="flex justify-between items-center mb-3">
                                        <div className="flex items-center text-gray-700">
                                            <span className="text-lg font-semibold mr-3">{oldCombinedValue}</span>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400"> <path d="M5 12h14"></path> <path d="m12 5 7 7-7 7"></path> </svg>
                                            <span className={`text-lg font-semibold ml-3 ${
                                                newNBack > oldNBack ? 'text-green-600' :
                                                newNBack < oldNBack ? 'text-red-600' :
                                                newDifficulty > oldDifficulty ? 'text-green-600' :
                                                newDifficulty < oldDifficulty ? 'text-red-600' : 'text-gray-700' // Same level
                                            }`}>
                                                {newCombinedValue}
                                            </span>
                                        </div>
                                        {/* --- MODIFICATION: Apply matching style for "Based on d'" --- */}
                                        <div className="text-xs text-gray-500">
                                            Based on d′: {formatNum(overallDprime, 2)}
                                        </div>
                                    </div>
                                    {/* Progression Messages */}
                                    {newNBack > oldNBack && (
                                        <div className="bg-green-50 border-l-4 border-green-400 p-3 text-green-800 text-sm rounded-r-md">
                                            <div className="flex items-start">
                                                <div className="text-green-500 mr-2 text-lg" aria-hidden="true">🎉</div>
                                                <div>Advanced to {newNBack}-back! Starting at {newCombinedValue}.</div>
                                            </div>
                                        </div>
                                    )}
                                    {newNBack < oldNBack && (
                                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 text-yellow-800 text-sm rounded-r-md">
                                            <div className="flex items-start">
                                                <div className="text-yellow-600 mr-2 text-lg" aria-hidden="true">⚠️</div>
                                                <div>Adjusting level: Moving to {newNBack}-back ({newCombinedValue}).</div>
                                            </div>
                                        </div>
                                    )}
                                    {/* Show coaching message if level maintained */}
                                    {newNBack === oldNBack && feedbackMessage && (
                                        <div className="mt-3">
                                            <CoachingMessage message={feedbackMessage} />
                                        </div>
                                    )}
                                    {/* Fallback if level maintained but no specific message */}
                                    {newNBack === oldNBack && !feedbackMessage && (
                                        <div className="mt-3 bg-gray-50 border-l-4 border-gray-400 p-3 text-gray-700 text-sm rounded-r-md">
                                            Level maintained at {newCombinedValue}. Keep practicing!
                                        </div>
                                    )}
                                </div>
                             )}
                             {/* 3. Failed Challenge Message (Conditional) */}
                            {isFailedChallengeAttempt && (
                                <div className="mb-6">
                                    <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-3 text-orange-800 rounded-r-md">
                                        <p className="font-medium">Challenge Attempt Unsuccessful</p>
                                        <p className="mt-1">
                                            Your progress level ({oldCombinedValue}) remains unchanged.
                                            {goodThreshold !== null ? ` Achieve d' ≥ ${formatNum(goodThreshold, 2)} on Level ${formatCombinedDifficulty(oldNBack, 0.95)} to advance.` : ' Try again to advance.'}
                                        </p>
                                    </div>
                                </div>
                             )}

                             {/* Challenge Success Message (Conditional) */}
                             {gameResults?.challengeSuccess && ( // Check optional chaining for safety
                                <div className="mb-6">
                                    <div className="bg-green-50 border-l-4 border-green-400 p-4 text-green-800 rounded-r-md">
                                        <p className="font-medium">Challenge Completed Successfully!</p>
                                        <p className="mt-1">
                                            Congratulations! You've advanced to {newNBack}-back training.
                                            Continue your progress to further improve your working memory.
                                        </p>
                                    </div>
                                </div>
                            )}
                            {/* 4. Feature Introduction Message (Conditional) */}
                            {introMessage && (
                                <div className="mb-6">
                                    <FeatureIntroMessage message={introMessage} />
                                </div>
                             )}
                        </div>
                    )}

                    {/* Detailed Stats Tab Content */}
                    {/* Apply overflow-y-auto here or to a wrapper if needed for mobile scroll */}
                    {activeTab === 'detailed' && (
                        <div className="animate-[fadeIn_0.3s_ease-in] max-h-[60vh] overflow-y-auto"> {/* Added max-h and overflow-y-auto for mobile scroll */}
                            {/* Visual/Audio Performance Details Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                                {/* Visual Performance Card */}
                                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                    <div className="flex items-center mb-3">
                                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-600" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>
                                        </div>
                                        <h4 className="font-semibold text-gray-800">Visual Performance</h4>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between"><span className="text-gray-600">Accuracy:</span><span className="font-medium text-gray-800">{formatAcc(visualAccuracy)}%</span></div>
                                        <div className="flex justify-between"><span className="text-gray-600">Hits:</span><span className="text-gray-800">{visualCorrect ?? '?'} / {visualTargetCount ?? '?'}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-600">False Alarms:</span><span className="text-gray-800">{visualFalseAlarms ?? '?'}</span></div>
                                        <div className="pt-2 border-t border-gray-100 mt-2">
                                            <div className="flex justify-between items-center"><span className="text-gray-600">d′ Score:</span><span className="font-medium text-blue-600 text-base">{formatNum(visualDprime, 2)}</span></div>
                                        </div>
                                    </div>
                                </div>
                                {/* Audio Performance Card */}
                                <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                    <div className="flex items-center mb-3">
                                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mr-2">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-600" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" /></svg>
                                        </div>
                                        <h4 className="font-semibold text-gray-800">Audio Performance</h4>
                                    </div>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between"><span className="text-gray-600">Accuracy:</span><span className="font-medium text-gray-800">{formatAcc(audioAccuracy)}%</span></div>
                                        <div className="flex justify-between"><span className="text-gray-600">Hits:</span><span className="text-gray-800">{audioCorrect ?? '?'} / {audioTargetCount ?? '?'}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-600">False Alarms:</span><span className="text-gray-800">{audioFalseAlarms ?? '?'}</span></div>
                                        <div className="pt-2 border-t border-gray-100 mt-2">
                                            <div className="flex justify-between items-center"><span className="text-gray-600">d′ Score:</span><span className="font-medium text-green-600 text-base">{formatNum(audioDprime, 2)}</span></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Session Details Card */}
                            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm mb-6">
                                <h3 className="text-sm font-medium text-gray-700 mb-3">Session Details</h3>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                                    <div> <div className="text-gray-500">Level Played</div> <div className="font-medium">{oldCombinedValue}</div> </div>
                                    <div> <div className="text-gray-500">Trials</div> <div className="font-medium">{trialsCompleted} / {totalTrialsPlanned}</div> </div>
                                    <div> <div className="text-gray-500">Duration</div> <div className="font-medium">{formatDuration(durationMs)}</div> </div>
                                    <div> <div className="text-gray-500">Overall Acc.</div> <div className="font-medium">{formatAcc(overallAccuracy)}%</div> </div>
                                </div>
                            </div>

                            {/* --- MODIFICATION START: Performance Scale Reference with refined highlighting --- */}
                             <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                 {/* Title and User's Score */}
                                 <div className="flex justify-between items-center mb-3">
                                    <h3 className="text-sm font-medium text-gray-700"> Performance Scale Reference (d′) </h3>
                                    {overallDprime !== null && (
                                        // --- Apply text-xs text-gray-500 here ---
                                        <span className="text-xs text-gray-500">
                                            Your d′: {formatNum(overallDprime, 2)}
                                        </span>
                                    )}
                                 </div>

                                { thresholdsValid ? ( // Only display if dynamic thresholds are valid
                                    <>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                                            {/* Column 1 */}
                                            <div className="space-y-2 text-sm">
                                                {/* Excellent */}
                                                <div className="flex items-center">
                                                    <div className="w-3 h-3 rounded-full mr-2 shrink-0 bg-green-500"></div>
                                                    {/* Label: Bold if active, standard gray */}
                                                    <span className={`min-w-[80px] text-gray-700 ${performanceCategory === 'excellent' ? 'font-semibold' : 'font-medium'}`}>Excellent:</span>
                                                    {/* Value: Purple and bold if active, standard gray otherwise */}
                                                    <span className={`ml-2 ${performanceCategory === 'excellent' ? 'text-indigo-600 font-semibold' : 'text-gray-600'}`}>≥ {formatNum(excellentThreshold, 2)}</span>
                                                </div>
                                                {/* Very Good */}
                                                <div className="flex items-center">
                                                    <div className="w-3 h-3 rounded-full mr-2 shrink-0 bg-blue-500"></div>
                                                    <span className={`min-w-[80px] text-gray-700 ${performanceCategory === 'very-good' ? 'font-semibold' : 'font-medium'}`}>Very Good:</span>
                                                    <span className={`ml-2 ${performanceCategory === 'very-good' ? 'text-indigo-600 font-semibold' : 'text-gray-600'}`}>{formatNum(veryGoodT, 2)} - {formatNum(excellentThreshold, 2)}</span>
                                                </div>
                                                {/* Good */}
                                                <div className="flex items-center">
                                                    <div className="w-3 h-3 rounded-full mr-2 shrink-0 bg-indigo-500"></div>
                                                    <span className={`min-w-[80px] text-gray-700 ${performanceCategory === 'good' ? 'font-semibold' : 'font-medium'}`}>Good:</span>
                                                    <span className={`ml-2 ${performanceCategory === 'good' ? 'text-indigo-600 font-semibold' : 'text-gray-600'}`}>{formatNum(goodThreshold, 2)} - {formatNum(veryGoodT, 2)}</span>
                                                </div>
                                            </div>
                                            {/* Column 2 */}
                                            <div className="space-y-2 text-sm">
                                                {/* Fair */}
                                                <div className="flex items-center">
                                                    <div className="w-3 h-3 rounded-full mr-2 shrink-0 bg-amber-500"></div>
                                                    <span className={`min-w-[80px] text-gray-700 ${performanceCategory === 'fair' ? 'font-semibold' : 'font-medium'}`}>Fair:</span>
                                                    <span className={`ml-2 ${performanceCategory === 'fair' ? 'text-indigo-600 font-semibold' : 'text-gray-600'}`}>{formatNum(fairThreshold, 2)} - {formatNum(goodThreshold, 2)}</span>
                                                </div>
                                                {/* Needs Improvement */}
                                                <div className="flex items-center">
                                                    <div className="w-3 h-3 rounded-full mr-2 shrink-0 bg-red-500"></div>
                                                    <span className={`min-w-[80px] text-gray-700 ${performanceCategory === 'needs-improvement' ? 'font-semibold' : 'font-medium'}`}>Needs Imp.:</span>
                                                    <span className={`ml-2 ${performanceCategory === 'needs-improvement' ? 'text-indigo-600 font-semibold' : 'text-gray-600'}`}>&lt; {formatNum(fairThreshold, 2)}</span>
                                                </div>
                                                 {/* Placeholder for alignment if needed & Unknown Category */}
                                                <div className="flex items-center h-[20px]"> {/* Match height of other items */}
                                                     {performanceCategory === 'unknown' && (
                                                        <>
                                                          <div className="w-3 h-3 rounded-full mr-2 shrink-0 bg-gray-500"></div>
                                                          {/* Label: Bold if active, standard gray */}
                                                          <span className="min-w-[80px] text-gray-700 font-semibold">Unknown</span>
                                                          {/* No range value for unknown */}
                                                        </>
                                                     )}
                                                </div>
                                            </div>
                                        </div>
                                        {/* --- Update explanatory note --- */}
                                        <p className="text-xs text-gray-500 mt-3">Note: These thresholds guided your level progression.</p>
                                    </>
                                ) : (
                                    <p className="text-sm text-gray-500">Performance scale reference thresholds not available for this session.</p>
                                )}
                            </div>
                            {/* --- MODIFICATION END --- */}
                        </div>
                    )}

                    {/* --- Commented out Tips & Progress Tab Content --- */}
                    {/* ... */}
                </div>

                {/* Action Buttons Footer */}
                <div className="px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
                    <div className={`grid grid-cols-1 ${bottomGridColsClass} gap-2 sm:gap-3`}>
                         {/* Play Next/Again Button (Primary Action) */}
                         <button
                            onClick={handleStartNextSessionAction} // ** FIXED **
                            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors order-1"
                            title={isPlayground ? "Start another playground session" : isFailedChallengeAttempt ? `Retry the challenge at ${oldCombinedValue}` : `Start the next session at level ${newCombinedValue}`}
                          >
                            <FontAwesomeIcon icon={faPlay} className="w-3.5 h-3.5 mr-1.5" />
                            {playAgainButtonText}
                         </button>

                         {/* Retry Level Button (Conditional) */}
                         {showRetryOption && (
                            <button
                                onClick={handleRetry} // Uses the dedicated handler
                                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors order-3 sm:order-2"
                                title={`Try level ${formatCombinedDifficulty(oldNBack, 0.0)} again from the start (difficulty 0.00)`}
                            >
                                <FontAwesomeIcon icon={faRotateRight} className="w-3.5 h-3.5 mr-1.5" />
                                Retry Level {oldNBack}.00
                            </button>
                         )}

                         {/* Watch Replay Button (Conditional) */}
                         {canReplay && (
                            <button
                                onClick={handleReplay} // ** FIXED **
                                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-colors order-4 sm:order-3"
                                title="Watch a replay of this game session"
                                disabled={!canReplay} // Explicitly disable if needed, though canReplay check handles click
                            >
                                <FontAwesomeIcon icon={faVideo} className="w-3.5 h-3.5 mr-1.5" />
                                Watch Replay
                            </button>
                         )}

                         {/* Dashboard Button */}
                         <button
                            onClick={onDashboard} // Assumes this prop is correctly passed and navigates
                            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors order-2 sm:order-last" // Adjusted order for consistency
                            title="Return to the main dashboard"
                         >
                             <FontAwesomeIcon icon={faHouse} className="w-3.5 h-3.5 mr-1.5" />
                            Dashboard
                         </button>
                    </div>
                </div>
            </div> {/* End Results Card */}

            {/* Comment Modal */}
            <CommentModal
                isOpen={isCommentModalOpen}
                onClose={handleCloseJournalModal}
                onSave={handleSaveJournalEntry}
                availableTags={activeProfile?.journalTags || []}
                title="Add Note & Tags to Session Summary"
                isSaving={isSavingToJournal}
                saveStatus={journalSaveStatus} // Pass status for display in modal if needed
            />
        </div> /* End Main Screen Container */
    );
};

export default GameResults;